import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { RegionConsensusPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/for/sre')({
  component: SrePage,
  head: () => ({
    meta: [
      { title: 'SRE Monitoring Tools — Less Noise, Track SLOs' },
      { name: 'description', content: 'Uptime monitoring for SREs: alert confirmation cuts noise, escalation policies, API-first, MCP integration. From $3/mo.' },
      { property: 'og:title', content: 'SRE Monitoring Tools — Less Alert Noise, SLOs | Uptrack' },
      { property: 'og:description', content: 'Alert confirmation, escalation policies, API-first, MCP integration. Built for SREs. Free plan, or paid plans from $3/mo.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-sre.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/sre' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For SRE', item: 'https://uptrack.app/for/sre' },
          ],
        }),
      },
    ],
  }),
})

function SrePage() {
  return (
    <FeaturePage
      eyebrow='For · SRE teams'
      section='SRE teams'
      title='Reliable monitoring for reliability engineers.'
      intro='Alert confirmation eliminates false positives. Escalation policies route to the right on-call. API-first design fits your existing workflows. Account and check data is stored in the EU.'
      featuresHeading='Tools that respect your on-call rotation'
      features={[
        { title: 'Alert confirmation', desc: 'Uptrack re-checks before firing alerts. Reduce alert fatigue from transient failures and network blips.' },
        { title: 'Escalation policies', desc: "Define who gets paged first, and who gets escalated to if the issue isn't acknowledged. Multi-tier support." },
        { title: 'Multi-channel alerts', desc: 'Email, Slack, Discord, webhooks. Route different severity levels to different channels.' },
        { title: 'API-first design', desc: 'Full REST API on every paid plan. Manage monitors programmatically. Integrate with your deployment pipeline.' },
        { title: 'MCP integration', desc: 'Connect to Claude or ChatGPT via MCP. Query monitor status, create monitors, and manage incidents through AI.' },
        { title: 'Data stored in the EU', desc: 'Account and check data is stored in Nuremberg, Germany. ' + MONITOR_LOCATIONS.sre },
      ]}
      why={{ heading: 'Fewer pages, faster resolution', body: "Your current monitoring fires 50 alerts a week, and 40 of them are false positives. Your team ignores alerts, and real incidents get missed. Uptrack's alert confirmation re-verifies failures before paging — so when your phone buzzes, it's real. Escalation policies make sure the right person responds, every time." }}
      cta={{ heading: '30-second checks on every plan', body: 'Full API access on paid plans, alert confirmation on every plan. Start free, or choose a plan for more.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/monitors/api">
          <RegionConsensusPiece />
        </ProductFrame>
      }
    />
  )
}
